import { useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ErrorText, StyledQuillWrapper, CharacterCount } from './Editor.styles';
import { FieldError } from 'react-hook-form';
import EditorAttachments from './EditorAttachments';

interface Props {
    value: string;
    onChange: (content: string) => void;
    error?: FieldError;
    maxCharacterCount?: number;
    disabled?: boolean;
    attachments?: File[];
    onFilesAttached?: (files: File[]) => void;
    onRemoveAttachment?: (index: number) => void;
}

const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

const QuillEditor = ({
    value,
    onChange,
    error,
    maxCharacterCount,
    disabled,
    attachments,
    onFilesAttached,
    onRemoveAttachment,
}: Props) => {
    const modules = {
        toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'clean'],
        ],
    };

    const wrapperRef = useRef<HTMLDivElement>(null);

    const currentLength = stripHtml(value || '').length;

    return (
        <StyledQuillWrapper error={error} ref={wrapperRef}>
            <ReactQuill
                value={value || ''}
                onChange={onChange}
                modules={modules}
                theme="snow"
                readOnly={disabled}
            />
            {onFilesAttached && (
                <EditorAttachments
                    wrapperRef={wrapperRef}
                    attachments={attachments}
                    onFilesAttached={onFilesAttached}
                    onRemoveAttachment={onRemoveAttachment}
                />
            )}
            {error && <ErrorText>{error.message}</ErrorText>}
            {maxCharacterCount && (
                <CharacterCount>
                    {currentLength}/{maxCharacterCount}
                </CharacterCount>
            )}
        </StyledQuillWrapper>
    );
};

export default QuillEditor;
